import { Box, Tooltip, styled } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";

import { SessionContext } from "../../Context/SessionContext";
import { PhoneNumber, PhoneNumberType } from "../../Models/PhoneNumber";
import { useApi } from "../../Utils/useApi";
import PhoneNumberField, { PhoneNumberFieldProps } from "../Helpers/PhoneNumberField";
import EditDotIcon from "./RibbonEditDotIcon";

interface RibbonPhoneNumberFieldProps extends PhoneNumberFieldProps {
  /** The value to revert to when the toggle button is selected. */
  revertValue?: string;
  /** Make this prop from `ValidatedTextFieldProps` required since this component must always be controlled. */
  value: string;
}

/** Extension of ValidatedTextField adding support for cached/revert value swap. */
const RibbonValidatedTextField: React.FC<RibbonPhoneNumberFieldProps> = ({
  customerKey,
  disabled,
  fullWidth,
  revertValue,
  value,
  onChange,
  onAccepted,
  ...rest
}) => {
  const { phoneNumberCache, setPhoneNumberCache, setPhoneNumberErrors } = useContext(SessionContext);
  const [internalValue, setInternalValue] = useState<string>("");
  const [cachedValue, setCachedValue] = useState<string>();
  const [queryPhoneNumber, setQueryPhoneNumber] = useState<string>();
  const queryPhoneNumberRef = useRef(queryPhoneNumber);
  const phoneNumberCacheRef = useRef(phoneNumberCache);

  useEffect(() => {
    phoneNumberCacheRef.current = phoneNumberCache;
  }, [phoneNumberCache]);
  useEffect(() => {
    setInternalValue(String(value));
    // if value is fully formed validate against a cached number, else query for the number
    if (!/\d{10}/.test(value)) return setPhoneNumberErrors(e => ({ ...e, [customerKey]: undefined }));
    const cachedNumber = phoneNumberCacheRef.current[value];
    if (cachedNumber !== undefined)
      setPhoneNumberErrors(e => ({
        ...e,
        [customerKey]: !cachedNumber || cachedNumber.type === PhoneNumberType["No Action"] ? undefined : cachedNumber
      }));
    else {
      queryPhoneNumberRef.current = value;
      setQueryPhoneNumber(value);
    }
  }, [value]);
  const { response } = useApi<PhoneNumber[]>({
    url: "Legacy/V0/PhoneNumbers",
    config: { params: { phoneNumber: queryPhoneNumber } },
    valid: Boolean(queryPhoneNumber)
  });
  useEffect(() => {
    if (!response || !queryPhoneNumberRef.current) return;
    const phoneNumber = response.at(0);
    // save response to the session cache
    if (phoneNumberCacheRef.current[queryPhoneNumberRef.current] === undefined) {
      phoneNumberCacheRef.current[queryPhoneNumberRef.current] = phoneNumber ?? null;
      setPhoneNumberCache(phoneNumberCacheRef.current);
    }
    setPhoneNumberErrors(e => ({
      ...e,
      [customerKey]: !phoneNumber || phoneNumber.type === PhoneNumberType["No Action"] ? undefined : phoneNumber
    }));
  }, [response]);

  const handleToggle = () => {
    if (cachedValue) {
      setInternalValue(cachedValue);
      onChange && onChange(String(cachedValue));
      onAccepted && onAccepted(String(cachedValue));
      setCachedValue(undefined);
    } else {
      setCachedValue(internalValue);
      setInternalValue(String(revertValue));
      onChange && onChange(String(revertValue));
      onAccepted && onAccepted(String(revertValue));
    }
  };

  const onFieldChange = (value?: string) => {
    if (cachedValue !== value) setCachedValue(undefined);
    setInternalValue(value ?? "");
    onChange && onChange(value);
  };

  return (
    <Box>
      {!disabled && ((revertValue !== undefined && revertValue != internalValue) || cachedValue) && (
        <Tooltip title={cachedValue ? `Switched value: ${cachedValue}` : `Previously submitted value: ${revertValue}`}>
          <EditDotIcon color={cachedValue ? "primary" : "secondary"} onClick={handleToggle} />
        </Tooltip>
      )}
      <StyledRibbonPhoneNumberField
        {...rest}
        customerKey={customerKey}
        isRevertDotShown={!disabled && revertValue !== undefined && revertValue !== value}
        fullWidth={fullWidth ?? true}
        disabled={disabled}
        value={internalValue}
        onChange={onFieldChange}
        onAccepted={onAccepted}
      />
    </Box>
  );
};

interface StyledRibbonPhoneNumberFieldProps extends PhoneNumberFieldProps {
  /** Flag indicating that the field has a revert value, and that the revert dot is shown. */
  isRevertDotShown: boolean;
}

/** A styled validated text field which outlines fields with the revert dot showing in a secondary color. */
const StyledRibbonPhoneNumberField = styled(PhoneNumberField, {
  shouldForwardProp: prop => prop !== "isRevertDotShown"
})<StyledRibbonPhoneNumberFieldProps>(({ error, isRevertDotShown, theme }) => ({
  "& .MuiOutlinedInput-notchedOutline":
    !error && isRevertDotShown
      ? {
          borderColor: theme.palette.secondary.main,
          borderWidth: ".15em"
        }
      : undefined
}));

export default RibbonValidatedTextField;
